import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { CatalogEntityCard } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { EmployeeSelect, isActiveEmployee } from "@/components/hr/EmployeeSelect";
import { SalaryPeriodFilterField } from "@/components/hr/SalaryPeriodMonthPicker";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useGridPagination } from "@/hooks/useGridPagination";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import {
  listEmployeeAllowanceTypeRecords,
  syncEmployeeAllowanceTypesFromApi,
} from "@/lib/hr/employeeAllowanceTypesStore";
import {
  listEmployeeDeductionTypeRecords,
  syncEmployeeDeductionTypesFromApi,
} from "@/lib/hr/employeeDeductionTypesStore";
import {
  createSalaryIncrementBatch,
  createSalaryIncrement,
  deleteSalaryIncrement,
  listAllSalaryIncrements,
  salaryIncrementApiErrorMessage,
  updateSalaryIncrement,
  type SalaryIncrementRecord,
  type SalaryIncrementType,
} from "@/lib/hr/salaryIncrementsStore";
import { cn } from "@/lib/utils";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { Banknote, CalendarIcon, CheckCircle2, Coins, Edit, Eye, Filter, LayoutGrid, List, Loader2, Plus, Search, Trash2, User } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

type Draft = {
  effectiveFrom: string;
  reason: string;
};

type IncrementSortBy = "updated_desc" | "updated_asc" | "effective_desc" | "effective_asc";

const emptyDraft = (): Draft => ({
  effectiveFrom: format(new Date(), "yyyy-MM-dd"),
  reason: "",
});

function incLabel(row: SalaryIncrementRecord): string {
  const sign = row.incrementType === "percentage" ? "%" : "";
  return `${row.incrementValue}${sign}`;
}

function employeeLabel(employees: EmployeeRecord[], employeeId: string): string {
  const e = employees.find((x) => x.id === employeeId);
  if (!e) return employeeId || "—";
  return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || e.id;
}

function employeePhoto(employees: EmployeeRecord[], employeeId: string): string | null {
  return employees.find((x) => x.id === employeeId)?.photoDataUrl?.trim() || null;
}

function employeeInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "E";
}

function EmployeeAvatar({ name, photoUrl }: { name: string; photoUrl: string | null }) {
  return (
    <div className="h-9 w-9 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
      {photoUrl ? (
        <img src={photoUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="text-xs font-bold text-primary">{employeeInitial(name)}</span>
      )}
    </div>
  );
}

function salaryDeltaLabel(row: SalaryIncrementRecord): string {
  const from = row.oldSalary || "0";
  const to = row.newSalary || "0";
  return `${from} → ${to}`;
}

function commaAmount(v: string | number | null | undefined): string {
  if (v == null) return "—";
  const raw = String(v).trim();
  if (!raw) return "—";
  const n = Number(raw.replace(/,/g, ""));
  if (!Number.isFinite(n)) return raw;
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n);
}

function parseAmountNumber(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const raw = String(v).trim();
  if (!raw) return null;
  const n = Number(raw.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function previewIncrementedAmount(params: {
  current: string | number | null | undefined;
  incrementType: SalaryIncrementType;
  incrementValue: string;
}): string | null {
  const cur = parseAmountNumber(params.current);
  const inc = parseAmountNumber(params.incrementValue);
  if (cur == null || inc == null) return null;
  const next = params.incrementType === "percentage" ? cur + (cur * inc) / 100 : cur + inc;
  return commaAmount(next);
}

function incrementAppliesToLabel(row: SalaryIncrementRecord): string {
  if (row.incrementTarget === "allowance") {
    return row.allowanceLabel?.trim() || (row.allowanceId ? `Allowance #${row.allowanceId}` : "Allowance");
  }
  if (row.incrementTarget === "deduction") {
    return row.deductionLabel?.trim() || (row.deductionId ? `Deduction #${row.deductionId}` : "Deduction");
  }
  return "Basic salary";
}

type SalaryIncrementDisplayItem =
  | { kind: "single"; row: SalaryIncrementRecord }
  | { kind: "batch"; batchId: string; rows: SalaryIncrementRecord[] };

function sortBatchRows(rs: SalaryIncrementRecord[]): SalaryIncrementRecord[] {
  const rank = (t: SalaryIncrementRecord["incrementTarget"]) => (t === "basic" ? 0 : t === "allowance" ? 1 : 2);
  return [...rs].sort((a, b) => {
    if (a.incrementTarget !== b.incrementTarget) return rank(a.incrementTarget) - rank(b.incrementTarget);
    const la = a.incrementTarget === "allowance" ? (a.allowanceLabel ?? "") : a.incrementTarget === "deduction" ? (a.deductionLabel ?? "") : "";
    const lb = b.incrementTarget === "allowance" ? (b.allowanceLabel ?? "") : b.incrementTarget === "deduction" ? (b.deductionLabel ?? "") : "";
    return la.localeCompare(lb);
  });
}

/** Rows without `increment_batch_id` (older data): group lines from the same save (same second + date + reason). */
function legacyRunGroupKey(r: SalaryIncrementRecord): string {
  let timePart = r.id;
  if (r.createdAt) {
    try {
      const d = parseISO(r.createdAt);
      if (isValid(d)) {
        timePart = format(d, "yyyy-MM-dd HH:mm:ss");
      }
    } catch {
      /* keep id so rows do not over-merge */
    }
  }
  return JSON.stringify([r.effectiveFrom, r.reason ?? "", timePart]);
}

function groupSalaryIncrementDisplayItems(rows: SalaryIncrementRecord[]): SalaryIncrementDisplayItem[] {
  const batchGroups = new Map<string, SalaryIncrementRecord[]>();
  const legacyGroups = new Map<string, SalaryIncrementRecord[]>();

  for (const r of rows) {
    if (r.incrementBatchId) {
      const list = batchGroups.get(r.incrementBatchId) ?? [];
      list.push(r);
      batchGroups.set(r.incrementBatchId, list);
    } else {
      const lk = legacyRunGroupKey(r);
      const list = legacyGroups.get(lk) ?? [];
      list.push(r);
      legacyGroups.set(lk, list);
    }
  }

  const seenBatch = new Set<string>();
  const seenLegacy = new Set<string>();
  const out: SalaryIncrementDisplayItem[] = [];

  for (const r of rows) {
    if (r.incrementBatchId) {
      if (seenBatch.has(r.incrementBatchId)) continue;
      seenBatch.add(r.incrementBatchId);
      out.push({
        kind: "batch",
        batchId: r.incrementBatchId,
        rows: sortBatchRows(batchGroups.get(r.incrementBatchId) ?? []),
      });
    } else {
      const lk = legacyRunGroupKey(r);
      if (seenLegacy.has(lk)) continue;
      seenLegacy.add(lk);
      const grouped = sortBatchRows(legacyGroups.get(lk) ?? []);
      if (grouped.length <= 1) {
        out.push({ kind: "single", row: grouped[0]! });
      } else {
        out.push({
          kind: "batch",
          batchId: `legacy-run:${lk}`,
          rows: grouped,
        });
      }
    }
  }

  return out;
}

function batchGroupTitle(rs: SalaryIncrementRecord[]): string {
  const hasBasic = rs.some((r) => r.incrementTarget === "basic");
  const allowanceCount = rs.filter((r) => r.incrementTarget === "allowance").length;
  const deductionCount = rs.filter((r) => r.incrementTarget === "deduction").length;
  const parts: string[] = [];
  if (hasBasic) parts.push("Basic salary");
  if (allowanceCount > 0) parts.push(`${allowanceCount} allowance${allowanceCount === 1 ? "" : "s"}`);
  if (deductionCount > 0) parts.push(`${deductionCount} deduction${deductionCount === 1 ? "" : "s"}`);
  return parts.join(" + ") || "Increment run";
}

function latestRowTimestamp(rows: SalaryIncrementRecord[]): string {
  if (!rows.length) return "";
  return rows.reduce((best, r) => (r.updatedAt > best ? r.updatedAt : best), rows[0]!.updatedAt);
}

type AllowanceBatchRow = {
  apply: boolean;
  incrementType: SalaryIncrementType;
  incrementValue: string;
};

type DeductionBatchRow = {
  apply: boolean;
  incrementType: SalaryIncrementType;
  incrementValue: string;
};

function buildAllowanceMap(emp: EmployeeRecord | undefined): Record<string, AllowanceBatchRow> {
  const m: Record<string, AllowanceBatchRow> = {};
  for (const a of emp?.allowances ?? []) {
    m[a.allowanceTypeId] = { apply: false, incrementType: "percentage", incrementValue: "" };
  }
  return m;
}

function buildDeductionMap(emp: EmployeeRecord | undefined): Record<string, DeductionBatchRow> {
  const m: Record<string, DeductionBatchRow> = {};
  for (const d of emp?.deductions ?? []) {
    m[d.deductionTypeId] = { apply: false, incrementType: "percentage", incrementValue: "" };
  }
  return m;
}

function allowanceDisplayName(
  emp: EmployeeRecord | undefined,
  allowanceTypes: { id: string; label: string }[],
  allowanceTypeId: string,
): string {
  const fromEmp = emp?.allowances?.find((x) => x.allowanceTypeId === allowanceTypeId)?.name;
  if (fromEmp) return fromEmp;
  const fromCatalog = allowanceTypes.find((x) => x.id === allowanceTypeId)?.label;
  return fromCatalog ?? "Allowance";
}

function deductionDisplayName(
  emp: EmployeeRecord | undefined,
  deductionTypes: { id: string; label: string }[],
  deductionTypeId: string,
): string {
  const fromEmp = emp?.deductions?.find((x) => x.deductionTypeId === deductionTypeId)?.name;
  if (fromEmp) return fromEmp;
  const fromCatalog = deductionTypes.find((x) => x.id === deductionTypeId)?.label;
  return fromCatalog ?? "Deduction";
}

export default function SalaryIncrements() {
  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);
  const activeEmployees = useMemo(() => employees.filter(isActiveEmployee), [employees]);

  const [allowanceTypesTick, setAllowanceTypesTick] = useState(0);
  const allowanceTypes = useMemo(() => listEmployeeAllowanceTypeRecords(), [allowanceTypesTick]);

  const [deductionTypesTick, setDeductionTypesTick] = useState(0);
  const deductionTypes = useMemo(() => listEmployeeDeductionTypeRecords(), [deductionTypesTick]);

  const [employeeId, setEmployeeId] = useState<string>("");
  const [modalEmployeeId, setModalEmployeeId] = useState<string>("");
  const [rows, setRows] = useState<SalaryIncrementRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterType, setFilterType] = useState<"all" | SalaryIncrementType>("all");
  const [filterTarget, setFilterTarget] = useState<"all" | "basic" | "allowance" | "deduction">("all");
  const [filterPeriodYear, setFilterPeriodYear] = useState<number | null>(null);
  const [filterPeriodMonth, setFilterPeriodMonth] = useState<number | null>(null);
  const [pickerYear, setPickerYear] = useState(() => new Date().getFullYear());
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [sortBy, setSortBy] = useState<IncrementSortBy>("updated_desc");
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const hasPeriodFilter = filterPeriodYear != null && filterPeriodMonth != null;

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SalaryIncrementRecord | null>(null);
  const [editingBatchRows, setEditingBatchRows] = useState<SalaryIncrementRecord[] | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [deleteTarget, setDeleteTarget] = useState<SalaryIncrementRecord | null>(null);
  const [deleteBatchRows, setDeleteBatchRows] = useState<SalaryIncrementRecord[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsRows, setDetailsRows] = useState<SalaryIncrementRecord[]>([]);

  const [applyBasic, setApplyBasic] = useState(false);
  const [basicBatchType, setBasicBatchType] = useState<SalaryIncrementType>("percentage");
  const [basicBatchValue, setBasicBatchValue] = useState("");
  const [allowanceMap, setAllowanceMap] = useState<Record<string, AllowanceBatchRow>>({});
  const [deductionMap, setDeductionMap] = useState<Record<string, DeductionBatchRow>>({});

  const modalSelectedEmployee = useMemo(() => {
    const shouldFallBackToPageEmployee = editing !== null || editingBatchRows !== null;
    const activeId = modalEmployeeId || (shouldFallBackToPageEmployee ? employeeId : "");
    if (!activeId) return undefined;
    return employees.find((e) => e.id === activeId);
  }, [employees, modalEmployeeId, employeeId, editing, editingBatchRows]);

  const modalEmployeeOptions = useMemo(() => {
    const selectedId = modalEmployeeId || modalSelectedEmployee?.id || "";
    if (!selectedId) return activeEmployees;
    if (activeEmployees.some((e) => e.id === selectedId)) return activeEmployees;
    const selected = employees.find((e) => e.id === selectedId);
    return selected ? [selected, ...activeEmployees] : activeEmployees;
  }, [activeEmployees, employees, modalEmployeeId, modalSelectedEmployee?.id]);

  useEffect(() => {
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, []);

  useEffect(() => {
    void syncEmployeeAllowanceTypesFromApi()
      .catch(() => {})
      .finally(() => setAllowanceTypesTick((t) => t + 1));
    void syncEmployeeDeductionTypesFromApi()
      .catch(() => {})
      .finally(() => setDeductionTypesTick((t) => t + 1));
  }, []);

  useEffect(() => {
    // keep employeeId only as a convenience for modal/edit actions
  }, [employees, employeeId]);

  const INCREMENTS_FETCH_CAP = 500;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAllSalaryIncrements({ page: 1, perPage: INCREMENTS_FETCH_CAP });
      setRows(res.rows);
    } catch (e) {
      toast.error(salaryIncrementApiErrorMessage(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const byBatch = new Map<string, SalaryIncrementRecord[]>();
    const byLegacy = new Map<string, SalaryIncrementRecord[]>();
    for (const r of rows) {
      if (r.incrementBatchId) {
        const list = byBatch.get(r.incrementBatchId) ?? [];
        list.push(r);
        byBatch.set(r.incrementBatchId, list);
      } else {
        const lk = legacyRunGroupKey(r);
        const list = byLegacy.get(lk) ?? [];
        list.push(r);
        byLegacy.set(lk, list);
      }
    }

    const q = query.trim().toLowerCase();

    const passesLine = (r: SalaryIncrementRecord) => {
      if (quickDate !== "all") {
        const updated = parseISO(r.updatedAt);
        if (!isValid(updated)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (updated < start || updated > end) return false;
      }
      if (filterTarget !== "all" && r.incrementTarget !== filterTarget) return false;
      if (filterType !== "all" && r.incrementType !== filterType) return false;
      if (hasPeriodFilter) {
        const effective = parseISO(r.effectiveFrom.length <= 10 ? `${r.effectiveFrom}T00:00:00` : r.effectiveFrom);
        if (!isValid(effective)) return false;
        if (effective.getFullYear() !== filterPeriodYear || effective.getMonth() + 1 !== filterPeriodMonth) {
          return false;
        }
      }
      if (!q) return true;
      const hay = `${r.incrementTarget} ${r.allowanceLabel ?? ""} ${r.incrementType} ${r.incrementValue} ${r.oldSalary} ${r.newSalary} ${r.effectiveFrom} ${r.reason ?? ""}`.toLowerCase();
      return hay.includes(q);
    };

    const includedIds = new Set<string>();
    for (const r of rows) {
      if (!passesLine(r)) continue;
      if (r.incrementBatchId) {
        const group = byBatch.get(r.incrementBatchId) ?? [r];
        for (const g of group) includedIds.add(g.id);
      } else {
        const lk = legacyRunGroupKey(r);
        const group = byLegacy.get(lk) ?? [r];
        for (const g of group) includedIds.add(g.id);
      }
    }

    return rows.filter((r) => includedIds.has(r.id));
  }, [rows, query, filterType, filterTarget, hasPeriodFilter, filterPeriodYear, filterPeriodMonth, quickDate]);

  const displayItems = useMemo(() => groupSalaryIncrementDisplayItems(filtered), [filtered]);

  const sortedDisplayItems = useMemo(() => {
    const items = [...displayItems];
    const updatedKey = (it: SalaryIncrementDisplayItem) =>
      it.kind === "single" ? it.row.updatedAt : latestRowTimestamp(it.rows);
    const effectiveKey = (it: SalaryIncrementDisplayItem) =>
      it.kind === "single" ? it.row.effectiveFrom : it.rows[0]?.effectiveFrom ?? "";
    items.sort((a, b) => {
      switch (sortBy) {
        case "updated_asc":
          return updatedKey(a).localeCompare(updatedKey(b));
        case "effective_desc":
          return effectiveKey(b).localeCompare(effectiveKey(a)) || updatedKey(b).localeCompare(updatedKey(a));
        case "effective_asc":
          return effectiveKey(a).localeCompare(effectiveKey(b)) || updatedKey(a).localeCompare(updatedKey(b));
        case "updated_desc":
        default:
          return updatedKey(b).localeCompare(updatedKey(a));
      }
    });
    return items;
  }, [displayItems, sortBy]);

  const appliedFiltersCount =
    (filterType !== "all" ? 1 : 0) + (filterTarget !== "all" ? 1 : 0) + (hasPeriodFilter ? 1 : 0);
  const resetFilters = () => {
    setFilterType("all");
    setFilterTarget("all");
    setFilterPeriodYear(null);
    setFilterPeriodMonth(null);
    setPickerYear(new Date().getFullYear());
  };

  const paginationKey = `${query}|${filterType}|${filterTarget}|${filterPeriodYear}|${filterPeriodMonth}|${quickDate}|${sortBy}`;
  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    paginatedItems,
    totalCount,
    totalPages,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sortedDisplayItems, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => {
    void (async () => {
      try {
        await syncEmployeesFromApi();
        setEmpTick((t) => t + 1);
      } catch {
        /* use cached list */
      }
      setModalEmployeeId("");
      setEditing(null);
      setEditingBatchRows(null);
      setDraft(emptyDraft());
      setApplyBasic(false);
      setBasicBatchType("percentage");
      setBasicBatchValue("");
      setAllowanceMap({});
      setDeductionMap({});
      setModalOpen(true);
    })();
  };

  const openEditBatch = (batchRows: SalaryIncrementRecord[]) => {
    void (async () => {
      const targetEmployeeId = batchRows[0]?.employeeId ?? "";
      if (!targetEmployeeId) {
        toast.error("Select an employee first.");
        return;
      }
      setEmployeeId(targetEmployeeId);
      try {
        await syncEmployeesFromApi();
        setEmpTick((t) => t + 1);
      } catch {
        /* use cached list */
      }
      const sorted = sortBatchRows(batchRows);
      const emp = listEmployees().find((e) => e.id === targetEmployeeId);
      const map = buildAllowanceMap(emp);
      const dmap = buildDeductionMap(emp);

      setModalEmployeeId(targetEmployeeId);
      setEditing(null);
      setEditingBatchRows(sorted);
      setDraft({
        effectiveFrom: sorted[0]?.effectiveFrom ?? "",
        reason: sorted[0]?.reason ?? "",
      });

      const basicRow = sorted.find((r) => r.incrementTarget === "basic");
      setApplyBasic(!!basicRow);
      if (basicRow) {
        setBasicBatchType(basicRow.incrementType);
        setBasicBatchValue(basicRow.incrementValue);
      } else {
        setBasicBatchType("percentage");
        setBasicBatchValue("");
      }
      for (const r of sorted) {
        if (r.incrementTarget === "allowance" && r.allowanceId) {
          map[r.allowanceId] = { apply: true, incrementType: r.incrementType, incrementValue: r.incrementValue };
        } else if (r.incrementTarget === "deduction" && r.deductionId) {
          dmap[r.deductionId] = { apply: true, incrementType: r.incrementType, incrementValue: r.incrementValue };
        }
      }
      setAllowanceMap(map);
      setDeductionMap(dmap);
      setModalOpen(true);
    })();
  };

  const openEdit = (r: SalaryIncrementRecord) => {
    void (async () => {
      const targetEmployeeId = r.employeeId;
      if (!targetEmployeeId) {
        toast.error("Select an employee first.");
        return;
      }
      setEmployeeId(targetEmployeeId);
      try {
        await syncEmployeesFromApi();
        setEmpTick((t) => t + 1);
      } catch {
        /* use cached list */
      }
      const emp = listEmployees().find((e) => e.id === targetEmployeeId);
      const map = buildAllowanceMap(emp);
      const dmap = buildDeductionMap(emp);

      setModalEmployeeId(targetEmployeeId);
      setEditingBatchRows(null);
      setEditing(r);
      setDraft({
        effectiveFrom: r.effectiveFrom,
        reason: r.reason ?? "",
      });

      if (r.incrementTarget === "allowance" && r.allowanceId) {
        setApplyBasic(false);
        setBasicBatchType("percentage");
        setBasicBatchValue("");
        const aid = r.allowanceId;
        map[aid] = { apply: true, incrementType: r.incrementType, incrementValue: r.incrementValue };
      } else if (r.incrementTarget === "deduction" && r.deductionId) {
        setApplyBasic(false);
        setBasicBatchType("percentage");
        setBasicBatchValue("");
        const did = r.deductionId;
        dmap[did] = { apply: true, incrementType: r.incrementType, incrementValue: r.incrementValue };
      } else {
        setApplyBasic(true);
        setBasicBatchType(r.incrementType);
        setBasicBatchValue(r.incrementValue);
      }
      setAllowanceMap(map);
      setDeductionMap(dmap);
      setModalOpen(true);
    })();
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setEditingBatchRows(null);
    setModalEmployeeId("");
    setDraft(emptyDraft());
    setApplyBasic(false);
    setBasicBatchType("percentage");
    setBasicBatchValue("");
    setAllowanceMap({});
    setDeductionMap({});
  };

  const openDetails = (rs: SalaryIncrementRecord[]) => {
    setDetailsRows(sortBatchRows(rs));
    setDetailsOpen(true);
  };

  const save = async () => {
    const activeEmployeeId = modalEmployeeId || employeeId;
    if (saving) return;
    if (!activeEmployeeId) {
      toast.error("Select an employee.");
      return;
    }
    if (!draft.effectiveFrom.trim()) {
      toast.error("Effective date is required.");
      return;
    }

    if (editingBatchRows?.length) {
      if (applyBasic) {
        if (!basicBatchValue.trim() || !Number.isFinite(Number(basicBatchValue))) {
          toast.error("Enter a valid basic salary increment value.");
          return;
        }
      }
      for (const r of editingBatchRows) {
        if (r.incrementTarget === "allowance" && r.allowanceId) {
          const row = allowanceMap[r.allowanceId];
          if (!row?.apply || !row.incrementValue.trim() || !Number.isFinite(Number(row.incrementValue))) {
            toast.error(
              `Enter a valid increment value for ${allowanceDisplayName(modalSelectedEmployee, allowanceTypes, r.allowanceId)}.`,
            );
            return;
          }
        } else if (r.incrementTarget === "deduction" && r.deductionId) {
          const row = deductionMap[r.deductionId];
          if (!row?.apply || !row.incrementValue.trim() || !Number.isFinite(Number(row.incrementValue))) {
            toast.error(
              `Enter a valid increment value for ${deductionDisplayName(modalSelectedEmployee, deductionTypes, r.deductionId)}.`,
            );
            return;
          }
        }
      }
    } else if (editing) {
      if (editing.incrementTarget === "allowance" && editing.allowanceId) {
        const row = allowanceMap[editing.allowanceId];
        if (!row?.incrementValue.trim() || !Number.isFinite(Number(row.incrementValue))) {
          toast.error("Increment value must be a number.");
          return;
        }
      } else if (editing.incrementTarget === "deduction" && editing.deductionId) {
        const row = deductionMap[editing.deductionId];
        if (!row?.incrementValue.trim() || !Number.isFinite(Number(row.incrementValue))) {
          toast.error("Increment value must be a number.");
          return;
        }
      } else {
        if (!basicBatchValue.trim() || !Number.isFinite(Number(basicBatchValue))) {
          toast.error("Increment value must be a number.");
          return;
        }
      }
    } else {
      const allowancePayload: Array<{ allowanceId: string; incrementType: SalaryIncrementType; incrementValue: string }> = [];
      for (const [aid, row] of Object.entries(allowanceMap)) {
        if (!row.apply) continue;
        if (!row.incrementValue.trim() || !Number.isFinite(Number(row.incrementValue))) {
          toast.error(
            `Enter a valid increment value for ${allowanceDisplayName(modalSelectedEmployee, allowanceTypes, aid)}.`,
          );
          return;
        }
        allowancePayload.push({
          allowanceId: aid,
          incrementType: row.incrementType,
          incrementValue: row.incrementValue,
        });
      }

      const deductionPayload: Array<{ deductionId: string; incrementType: SalaryIncrementType; incrementValue: string }> = [];
      for (const [did, row] of Object.entries(deductionMap)) {
        if (!row.apply) continue;
        if (!row.incrementValue.trim() || !Number.isFinite(Number(row.incrementValue))) {
          toast.error(
            `Enter a valid increment value for ${deductionDisplayName(modalSelectedEmployee, deductionTypes, did)}.`,
          );
          return;
        }
        deductionPayload.push({
          deductionId: did,
          incrementType: row.incrementType,
          incrementValue: row.incrementValue,
        });
      }

      let basicPayload: { incrementType: SalaryIncrementType; incrementValue: string } | null = null;
      if (applyBasic) {
        if (!basicBatchValue.trim() || !Number.isFinite(Number(basicBatchValue))) {
          toast.error("Enter a valid basic salary increment value.");
          return;
        }
        basicPayload = { incrementType: basicBatchType, incrementValue: basicBatchValue };
      }

      if (!basicPayload && allowancePayload.length === 0 && deductionPayload.length === 0) {
        toast.error("Select basic salary and/or at least one allowance or deduction to increment.");
        return;
      }
    }

    setSaving(true);
    try {
      // If user changed employee while editing, "move" increments by recreating them under the new employee,
      // then deleting the original rows.
      const originalEmployeeId = editingBatchRows?.[0]?.employeeId ?? editing?.employeeId ?? activeEmployeeId;
      const isMove = (editingBatchRows?.length || editing) && originalEmployeeId !== activeEmployeeId;

      if (isMove) {
        if (editingBatchRows?.length) {
          const allowancePayload: Array<{ allowanceId: string; incrementType: SalaryIncrementType; incrementValue: string }> = [];
          for (const [aid, row] of Object.entries(allowanceMap)) {
            if (!row.apply) continue;
            allowancePayload.push({ allowanceId: aid, incrementType: row.incrementType, incrementValue: row.incrementValue });
          }

          const deductionPayload: Array<{ deductionId: string; incrementType: SalaryIncrementType; incrementValue: string }> = [];
          for (const [did, row] of Object.entries(deductionMap)) {
            if (!row.apply) continue;
            deductionPayload.push({ deductionId: did, incrementType: row.incrementType, incrementValue: row.incrementValue });
          }

          let basicPayload: { incrementType: SalaryIncrementType; incrementValue: string } | null = null;
          if (applyBasic) basicPayload = { incrementType: basicBatchType, incrementValue: basicBatchValue };

          await createSalaryIncrementBatch({
            employeeId: activeEmployeeId,
            effectiveFrom: draft.effectiveFrom,
            reason: draft.reason.trim() ? draft.reason.trim() : null,
            basic: basicPayload,
            allowances: allowancePayload,
            deductions: deductionPayload,
          });

          for (const r of editingBatchRows) {
            await deleteSalaryIncrement({ employeeId: r.employeeId, id: r.id });
          }

          toast.success("Increment run moved.");
        } else if (editing) {
          const reason = draft.reason.trim() ? draft.reason.trim() : null;
          const payload =
            editing.incrementTarget === "allowance" && editing.allowanceId
              ? {
                  incrementTarget: "allowance" as const,
                  allowanceId: editing.allowanceId,
                  deductionId: null,
                  incrementType: allowanceMap[editing.allowanceId]!.incrementType,
                  incrementValue: allowanceMap[editing.allowanceId]!.incrementValue,
                }
              : editing.incrementTarget === "deduction" && editing.deductionId
                ? {
                    incrementTarget: "deduction" as const,
                    allowanceId: null,
                    deductionId: editing.deductionId,
                    incrementType: deductionMap[editing.deductionId]!.incrementType,
                    incrementValue: deductionMap[editing.deductionId]!.incrementValue,
                  }
                : {
                    incrementTarget: "basic" as const,
                    allowanceId: null,
                    deductionId: null,
                    incrementType: basicBatchType,
                    incrementValue: basicBatchValue,
                  };

          await createSalaryIncrement({
            employeeId: activeEmployeeId,
            incrementTarget: payload.incrementTarget,
            allowanceId: payload.allowanceId,
            deductionId: payload.deductionId,
            incrementType: payload.incrementType,
            incrementValue: payload.incrementValue,
            effectiveFrom: draft.effectiveFrom,
            reason: reason ?? undefined,
          });

          await deleteSalaryIncrement({ employeeId: editing.employeeId, id: editing.id });
          toast.success("Increment moved.");
        }

        closeModal();
        setPage(1);
        await load();
        void syncEmployeesFromApi()
          .catch(() => {})
          .finally(() => setEmpTick((t) => t + 1));
        return;
      }

      if (editingBatchRows?.length) {
        const reason = draft.reason.trim() ? draft.reason.trim() : null;
        const ordered = sortBatchRows(editingBatchRows);
        for (const r of ordered) {
          if (r.incrementTarget !== "basic") continue;
          await updateSalaryIncrement({
            employeeId: activeEmployeeId,
            id: r.id,
            patch: {
              incrementType: basicBatchType,
              incrementValue: basicBatchValue,
              effectiveFrom: draft.effectiveFrom,
              reason,
            },
          });
        }
        for (const r of ordered) {
          if (r.incrementTarget !== "allowance" || !r.allowanceId) continue;
          const ar = allowanceMap[r.allowanceId]!;
          await updateSalaryIncrement({
            employeeId: activeEmployeeId,
            id: r.id,
            patch: {
              incrementType: ar.incrementType,
              incrementValue: ar.incrementValue,
              effectiveFrom: draft.effectiveFrom,
              reason,
            },
          });
        }
        toast.success("Increment run updated.");
      } else if (editing) {
        const reason = draft.reason.trim() ? draft.reason.trim() : null;
        const patch =
          editing.incrementTarget === "allowance" && editing.allowanceId
            ? {
                incrementType: allowanceMap[editing.allowanceId]!.incrementType,
                incrementValue: allowanceMap[editing.allowanceId]!.incrementValue,
                effectiveFrom: draft.effectiveFrom,
                reason,
              }
            : editing.incrementTarget === "deduction" && editing.deductionId
              ? {
                  incrementType: deductionMap[editing.deductionId]!.incrementType,
                  incrementValue: deductionMap[editing.deductionId]!.incrementValue,
                  effectiveFrom: draft.effectiveFrom,
                  reason,
                }
            : {
                incrementType: basicBatchType,
                incrementValue: basicBatchValue,
                effectiveFrom: draft.effectiveFrom,
                reason,
              };
        const result = await updateSalaryIncrement({
          employeeId: activeEmployeeId,
          id: editing.id,
          patch,
        });
        toast.success(result.message);
      } else {
        const allowancePayload: Array<{ allowanceId: string; incrementType: SalaryIncrementType; incrementValue: string }> = [];
        for (const [aid, row] of Object.entries(allowanceMap)) {
          if (!row.apply) continue;
          allowancePayload.push({
            allowanceId: aid,
            incrementType: row.incrementType,
            incrementValue: row.incrementValue,
          });
        }
        const deductionPayload: Array<{ deductionId: string; incrementType: SalaryIncrementType; incrementValue: string }> = [];
        for (const [did, row] of Object.entries(deductionMap)) {
          if (!row.apply) continue;
          deductionPayload.push({
            deductionId: did,
            incrementType: row.incrementType,
            incrementValue: row.incrementValue,
          });
        }
        let basicPayload: { incrementType: SalaryIncrementType; incrementValue: string } | null = null;
        if (applyBasic) {
          basicPayload = { incrementType: basicBatchType, incrementValue: basicBatchValue };
        }
        const result = await createSalaryIncrementBatch({
          employeeId: activeEmployeeId,
          effectiveFrom: draft.effectiveFrom,
          reason: draft.reason.trim() ? draft.reason.trim() : null,
          basic: basicPayload,
          allowances: allowancePayload,
          deductions: deductionPayload,
        });
        toast.success(result.message);
      }
      closeModal();
      setPage(1);
      if (activeEmployeeId !== employeeId) setEmployeeId(activeEmployeeId);
      await load();
      void syncEmployeesFromApi()
        .catch(() => {})
        .finally(() => setEmpTick((t) => t + 1));
    } catch (e) {
      toast.error(salaryIncrementApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!employeeId || deleting) return;
    if (deleteBatchRows?.length) {
      setDeleting(true);
      try {
        for (const r of deleteBatchRows) {
          await deleteSalaryIncrement({ employeeId, id: r.id });
        }
        toast.success(deleteBatchRows.length === 1 ? "Increment deleted." : `${deleteBatchRows.length} increment records deleted.`);
        setDeleteBatchRows(null);
        setPage(1);
        await load();
      } catch (e) {
        toast.error(salaryIncrementApiErrorMessage(e));
      } finally {
        setDeleting(false);
      }
      return;
    }
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await deleteSalaryIncrement({ employeeId, id: deleteTarget.id });
      toast.success(result.message);
      setDeleteTarget(null);
      setPage(1);
      await load();
    } catch (e) {
      toast.error(salaryIncrementApiErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  const lockLineSelection = editing !== null || editingBatchRows !== null;
  const isSingleRowEdit = editing !== null && editingBatchRows === null;
  const editBasicOnly = isSingleRowEdit && editing!.incrementTarget === "basic";
  const editAllowanceId =
    isSingleRowEdit && editing!.incrementTarget === "allowance" && editing!.allowanceId ? editing!.allowanceId : null;
  const editDeductionId =
    isSingleRowEdit && editing!.incrementTarget === "deduction" && editing!.deductionId ? editing!.deductionId : null;
  const isBatchEditModal = (editingBatchRows?.length ?? 0) > 0;

  const editingLineFor = useCallback(
    (target: SalaryIncrementRecord["incrementTarget"], refId?: string | null): SalaryIncrementRecord | null => {
      if (editingBatchRows?.length) {
        if (target === "basic") return editingBatchRows.find((r) => r.incrementTarget === "basic") ?? null;
        if (target === "allowance") return editingBatchRows.find((r) => r.incrementTarget === "allowance" && r.allowanceId === refId) ?? null;
        if (target === "deduction") return editingBatchRows.find((r) => r.incrementTarget === "deduction" && r.deductionId === refId) ?? null;
        return null;
      }
      if (editing) {
        if (target === "basic" && editing.incrementTarget === "basic") return editing;
        if (target === "allowance" && editing.incrementTarget === "allowance" && editing.allowanceId === refId) return editing;
        if (target === "deduction" && editing.incrementTarget === "deduction" && editing.deductionId === refId) return editing;
      }
      return null;
    },
    [editingBatchRows, editing],
  );

  return (
    <Layout title="Salary increments">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Salary increments</h1>
            <p className="text-sm text-muted-foreground mt-1">Maintain effective-dated salary history for employees.</p>
          </div>
          <Button variant="premium" className="gap-2" onClick={openCreate}>
            <Plus className="w-4 h-4" />
            Add increment
          </Button>
        </div>

        <Dialog
          open={modalOpen}
          onOpenChange={(o) => {
            if (!o) closeModal();
            else setModalOpen(true);
          }}
        >
          <DialogContent className="max-w-[820px] w-[95vw] max-h-[90vh] overflow-y-auto p-6 sm:max-w-[820px]">
            <DialogHeader className="pb-3 border-b border-border/50">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
                  <Coins className="w-5 h-5" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold text-foreground">
                    {isBatchEditModal ? "Edit increment" : editing ? "Edit salary increment" : "New salary & allowance increment"}
                  </DialogTitle>
                  <p className="text-xs text-muted-foreground">
                    {isBatchEditModal ? (
                      <>Update basic and allowance lines together; effective date and reason apply to every record in the batch.</>
                    ) : editing ? (
                      <>
                        Editing <span className="text-foreground font-medium">{incrementAppliesToLabel(editing)}</span> only; other lines are
                        locked.
                      </>
                    ) : (
                      <>Apply basic, allowance, and deduction changes with one effective date.</>
                    )}
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="grid grid-cols-1 gap-4 py-1">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-foreground">
                    Employee <span className="text-destructive">*</span>
                  </Label>
                  <EmployeeSelect
                    value={modalEmployeeId}
                    onChange={(id) => {
                      setModalEmployeeId(id);
                      const e = employees.find((x) => x.id === id);
                      if (e) {
                        setAllowanceMap(buildAllowanceMap(e));
                        setDeductionMap(buildDeductionMap(e));
                      }
                      if (!editing && !isBatchEditModal) {
                        setApplyBasic(false);
                        setBasicBatchType("percentage");
                        setBasicBatchValue("");
                      }
                    }}
                    employees={employees}
                    options={modalEmployeeOptions}
                    placeholder="Select employee…"
                    triggerClassName="h-10"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-foreground">
                      {editing || isBatchEditModal ? (
                        <>
                          Effective from <span className="text-destructive">*</span>
                        </>
                      ) : (
                        <>
                          Effective from (all lines in this run) <span className="text-destructive">*</span>
                        </>
                      )}
                    </Label>
                    <DatePartsPicker
                      value={draft.effectiveFrom}
                      onChange={(iso) => setDraft((x) => ({ ...x, effectiveFrom: iso }))}
                      fromYear={1980}
                      toYear={new Date().getFullYear() + 5}
                      placeholder="Select effective date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-foreground">Reason (optional)</Label>
                    <Input
                      className="glass-input h-10"
                      value={draft.reason}
                      onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
                      placeholder="e.g. Annual increment"
                    />
                  </div>
                </div>

                <div className="rounded-xl border border-border/50 bg-muted/15 p-3 space-y-3">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="inc-basic"
                      checked={applyBasic}
                      onCheckedChange={(v) => {
                        if (lockLineSelection) return;
                        setApplyBasic(v === true);
                      }}
                      disabled={lockLineSelection}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <Label htmlFor="inc-basic" className="text-sm font-semibold text-foreground cursor-pointer flex items-center gap-2">
                          <Banknote className="w-4 h-4 text-emerald-600 shrink-0" />
                          Basic salary
                        </Label>
                        <div className="text-xs text-muted-foreground shrink-0">
                          {(() => {
                            const base = editingLineFor("basic")?.oldSalary ?? modalSelectedEmployee?.basicSalary;
                            const cur = commaAmount(base);
                            const next =
                              applyBasic
                                ? previewIncrementedAmount({
                                    current: base,
                                    incrementType: basicBatchType,
                                    incrementValue: basicBatchValue,
                                  })
                                : null;
                            return (
                              <span className="font-mono">
                                <span className="text-foreground">{cur}</span>
                                {next ? (
                                  <>
                                    <span className="text-muted-foreground"> → </span>
                                    <span className="font-semibold text-emerald-600">{next}</span>
                                  </>
                                ) : null}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="flex flex-1 min-w-0">
                          <Select
                            value={basicBatchType}
                            onValueChange={(v) => setBasicBatchType(v as SalaryIncrementType)}
                            disabled={!applyBasic || (isSingleRowEdit && !editBasicOnly)}
                          >
                            <SelectTrigger
                              className="glass-input h-10 w-[180px] rounded-r-none border-r-0"
                              aria-label="Basic salary increment type"
                            >
                              <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percentage">Percentage</SelectItem>
                              <SelectItem value="fixed">Fixed amount</SelectItem>
                            </SelectContent>
                          </Select>
                          <div className="relative flex-1 min-w-0">
                            <Input
                              className="glass-input h-10 w-full rounded-l-none pr-12"
                              value={basicBatchValue}
                              onChange={(e) => setBasicBatchValue(e.target.value)}
                              placeholder={basicBatchType === "percentage" ? "Value (e.g. 10)" : "Value (e.g. 5000)"}
                              inputMode="decimal"
                              disabled={!applyBasic || (isSingleRowEdit && !editBasicOnly)}
                              aria-label="Basic salary increment value"
                            />
                            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                              {basicBatchType === "percentage" ? "%" : "Amt"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-3 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-foreground min-w-0">
                        <Coins className="w-4 h-4 text-amber-600 shrink-0" />
                        <span className="truncate">Allowances</span>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <Select
                          value=""
                          onValueChange={(v) => {
                            if (!v) return;
                            setAllowanceMap((m) => (m[v] ? m : { ...m, [v]: { apply: false, incrementType: "percentage", incrementValue: "" } }));
                          }}
                          disabled={!modalSelectedEmployee}
                        >
                          <SelectTrigger className="glass-input h-9 w-[200px]" aria-label="Add allowance type">
                            <SelectValue placeholder="Add allowance…" />
                          </SelectTrigger>
                          <SelectContent>
                            {allowanceTypes
                              .filter((t) => !(t.id in allowanceMap))
                              .map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.label}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="glass"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => {}}
                          disabled
                          aria-label="Add allowance"
                          title="Use dropdown to add"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {Object.keys(allowanceMap).length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No allowances in this increment. Use the + dropdown to add one.
                      </p>
                    ) : (
                      <div className="space-y-3 max-h-[min(50vh,420px)] overflow-y-auto pr-1">
                        {Object.keys(allowanceMap)
                          .sort((a, b) => allowanceDisplayName(modalSelectedEmployee, allowanceTypes, a).localeCompare(allowanceDisplayName(modalSelectedEmployee, allowanceTypes, b)))
                          .map((allowanceTypeId) => {
                            const row =
                              allowanceMap[allowanceTypeId] ?? { apply: false, incrementType: "percentage" as const, incrementValue: "" };
                            const rowLocked =
                              isSingleRowEdit &&
                              (editBasicOnly || (editAllowanceId !== null && editAllowanceId !== allowanceTypeId));
                            const empAllowance = modalSelectedEmployee?.allowances?.find((x) => x.allowanceTypeId === allowanceTypeId);
                            const base = editingLineFor("allowance", allowanceTypeId)?.oldSalary ?? empAllowance?.amount ?? 0;
                            const name = allowanceDisplayName(modalSelectedEmployee, allowanceTypes, allowanceTypeId);
                            return (
                              <div key={allowanceTypeId} className="rounded-lg border border-border/50 bg-background/60 p-2.5 space-y-2">
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  id={`allow-${allowanceTypeId}`}
                                  checked={row.apply}
                                  onCheckedChange={(v) => {
                                    if (lockLineSelection) return;
                                    setAllowanceMap((m) => ({
                                      ...m,
                                      [allowanceTypeId]: { ...row, apply: v === true },
                                    }));
                                  }}
                                  disabled={lockLineSelection}
                                  className="mt-1"
                                />

                                <div className="min-w-0 flex-1 space-y-2">
                                  <div className="flex items-center justify-between gap-3">
                                    <Label
                                      htmlFor={`allow-${allowanceTypeId}`}
                                      className="text-sm font-semibold text-foreground cursor-pointer truncate min-w-0"
                                    >
                                      {name}
                                    </Label>

                                    {(() => {
                                      const cur = commaAmount(base);
                                      const next = row.apply
                                        ? previewIncrementedAmount({
                                            current: base,
                                            incrementType: row.incrementType,
                                            incrementValue: row.incrementValue,
                                          })
                                        : null;
                                      return (
                                        <div className="text-xs text-muted-foreground shrink-0">
                                          <span className="font-mono">
                                            <span className="text-foreground">{cur}</span>
                                            {next ? (
                                              <>
                                                <span className="text-muted-foreground"> → </span>
                                                <span className="font-semibold text-emerald-600">{next}</span>
                                              </>
                                            ) : null}
                                          </span>
                                        </div>
                                      );
                                    })()}
                                  </div>

                                  <div className="flex flex-1 min-w-0">
                                    <Select
                                      value={row.incrementType}
                                      onValueChange={(v) =>
                                        setAllowanceMap((m) => ({
                                          ...m,
                                          [allowanceTypeId]: { ...row, incrementType: v as SalaryIncrementType },
                                        }))
                                      }
                                      disabled={!row.apply || rowLocked}
                                    >
                                      <SelectTrigger
                                        className="glass-input h-10 w-[180px] rounded-r-none border-r-0"
                                        aria-label={`${name} increment type`}
                                      >
                                        <SelectValue placeholder="Type" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="percentage">Percentage</SelectItem>
                                        <SelectItem value="fixed">Fixed</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <div className="relative flex-1 min-w-0">
                                      <Input
                                        className="glass-input h-10 w-full rounded-l-none pr-12"
                                        value={row.incrementValue}
                                        onChange={(e) =>
                                          setAllowanceMap((m) => ({
                                            ...m,
                                            [allowanceTypeId]: { ...row, incrementValue: e.target.value },
                                          }))
                                        }
                                        placeholder={row.incrementType === "percentage" ? "Value (e.g. 10)" : "Value (e.g. 500)"}
                                        inputMode="decimal"
                                        disabled={!row.apply || rowLocked}
                                        aria-label={`${name} increment value`}
                                      />
                                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                                        {row.incrementType === "percentage" ? "%" : "Amt"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl border border-border/50 bg-muted/15 p-3 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-foreground min-w-0">
                        <Coins className="w-4 h-4 text-sky-600 shrink-0" />
                        <span className="truncate">Deductions</span>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <Select
                          value=""
                          onValueChange={(v) => {
                            if (!v) return;
                            setDeductionMap((m) => (m[v] ? m : { ...m, [v]: { apply: false, incrementType: "percentage", incrementValue: "" } }));
                          }}
                          disabled={!modalSelectedEmployee}
                        >
                          <SelectTrigger className="glass-input h-9 w-[200px]" aria-label="Add deduction type">
                            <SelectValue placeholder="Add deduction…" />
                          </SelectTrigger>
                          <SelectContent>
                            {deductionTypes
                              .filter((t) => !(t.id in deductionMap))
                              .map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.label}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="glass"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => {}}
                          disabled
                          aria-label="Add deduction"
                          title="Use dropdown to add"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {Object.keys(deductionMap).length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No deductions in this increment. Use the + dropdown to add one.
                      </p>
                    ) : (
                      <div className="space-y-3 max-h-[min(50vh,420px)] overflow-y-auto pr-1">
                        {Object.keys(deductionMap)
                          .sort((a, b) => deductionDisplayName(modalSelectedEmployee, deductionTypes, a).localeCompare(deductionDisplayName(modalSelectedEmployee, deductionTypes, b)))
                          .map((deductionTypeId) => {
                            const row =
                              deductionMap[deductionTypeId] ?? { apply: false, incrementType: "percentage" as const, incrementValue: "" };
                            const rowLocked =
                              isSingleRowEdit &&
                              (editBasicOnly || (editDeductionId !== null && editDeductionId !== deductionTypeId));
                            const empDeduction = modalSelectedEmployee?.deductions?.find((x) => x.deductionTypeId === deductionTypeId);
                            const base = editingLineFor("deduction", deductionTypeId)?.oldSalary ?? empDeduction?.amount ?? 0;
                            const name = deductionDisplayName(modalSelectedEmployee, deductionTypes, deductionTypeId);
                            return (
                              <div key={deductionTypeId} className="rounded-lg border border-border/50 bg-background/60 p-2.5 space-y-2">
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  id={`deduct-${deductionTypeId}`}
                                  checked={row.apply}
                                  onCheckedChange={(v) => {
                                    if (lockLineSelection) return;
                                    setDeductionMap((m) => ({
                                      ...m,
                                      [deductionTypeId]: { ...row, apply: v === true },
                                    }));
                                  }}
                                  disabled={lockLineSelection}
                                  className="mt-1"
                                />

                                <div className="min-w-0 flex-1 space-y-2">
                                  <div className="flex items-center justify-between gap-3">
                                    <Label
                                      htmlFor={`deduct-${deductionTypeId}`}
                                      className="text-sm font-semibold text-foreground cursor-pointer truncate min-w-0"
                                    >
                                      {name}
                                    </Label>

                                    {(() => {
                                      const cur = commaAmount(base);
                                      const next = row.apply
                                        ? previewIncrementedAmount({
                                            current: base,
                                            incrementType: row.incrementType,
                                            incrementValue: row.incrementValue,
                                          })
                                        : null;
                                      return (
                                        <div className="text-xs text-muted-foreground shrink-0">
                                          <span className="font-mono">
                                            <span className="text-foreground">{cur}</span>
                                            {next ? (
                                              <>
                                                <span className="text-muted-foreground"> → </span>
                                                <span className="font-semibold text-emerald-600">{next}</span>
                                              </>
                                            ) : null}
                                          </span>
                                        </div>
                                      );
                                    })()}
                                  </div>

                                  <div className="flex flex-1 min-w-0">
                                    <Select
                                      value={row.incrementType}
                                      onValueChange={(v) =>
                                        setDeductionMap((m) => ({
                                          ...m,
                                          [deductionTypeId]: { ...row, incrementType: v as SalaryIncrementType },
                                        }))
                                      }
                                      disabled={!row.apply || rowLocked}
                                    >
                                      <SelectTrigger
                                        className="glass-input h-10 w-[180px] rounded-r-none border-r-0"
                                        aria-label={`${name} increment type`}
                                      >
                                        <SelectValue placeholder="Type" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="percentage">Percentage</SelectItem>
                                        <SelectItem value="fixed">Fixed</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <div className="relative flex-1 min-w-0">
                                      <Input
                                        className="glass-input h-10 w-full rounded-l-none pr-12"
                                        value={row.incrementValue}
                                        onChange={(e) =>
                                          setDeductionMap((m) => ({
                                            ...m,
                                            [deductionTypeId]: { ...row, incrementValue: e.target.value },
                                          }))
                                        }
                                        placeholder={row.incrementType === "percentage" ? "Value (e.g. 10)" : "Value (e.g. 500)"}
                                        inputMode="decimal"
                                        disabled={!row.apply || rowLocked}
                                        aria-label={`${name} increment value`}
                                      />
                                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                                        {row.incrementType === "percentage" ? "%" : "Amt"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
            </div>

            <DialogFooter className="pt-4 border-t border-border/50 gap-2">
              <Button variant="outline" onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={() => void save()}
                disabled={saving}
                className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-5"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Save Increment
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="glass-card p-6 space-y-4">
          <div className="space-y-1.5">
            <Label>Search</Label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  className="glass-input pl-9 h-10"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search reason, value, effective date…"
                />
              </div>
              <Sheet
                open={filtersOpen}
                onOpenChange={(open) => {
                  setFiltersOpen(open);
                  if (open && filterPeriodYear != null) {
                    setPickerYear(filterPeriodYear);
                  }
                }}
              >
                <SheetTrigger asChild>
                  <Button type="button" variant="glass" className="gap-2 shrink-0">
                    <Filter className="w-4 h-4" />
                    Filters
                    {appliedFiltersCount > 0 ? (
                      <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        {appliedFiltersCount}
                      </span>
                    ) : null}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Filter increments</SheetTitle>
                    <SheetDescription>Refine by target, increment type, and salary period.</SheetDescription>
                  </SheetHeader>

                  <div className="mt-6 space-y-5">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Applies to</Label>
                      <Select value={filterTarget} onValueChange={(v) => setFilterTarget(v as never)}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="basic">Basic salary</SelectItem>
                          <SelectItem value="allowance">Allowance</SelectItem>
                          <SelectItem value="deduction">Deduction</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Increment type</Label>
                      <Select value={filterType} onValueChange={(v) => setFilterType(v as never)}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="percentage">Percentage</SelectItem>
                          <SelectItem value="fixed">Fixed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <SalaryPeriodFilterField
                      label="Effective period"
                      selectedYear={filterPeriodYear}
                      selectedMonth={filterPeriodMonth}
                      onChange={(year, month) => {
                        setFilterPeriodYear(year);
                        setFilterPeriodMonth(month);
                      }}
                      browseYear={pickerYear}
                      onBrowseYearChange={setPickerYear}
                    />
                  </div>

                  <SheetFooter className="mt-6 gap-2">
                    <Button type="button" variant="glass" className="w-full" onClick={resetFilters}>
                      Reset
                    </Button>
                    <SheetClose asChild>
                      <Button type="button" variant="premium" className="w-full">
                        Apply filters
                      </Button>
                    </SheetClose>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div className="shrink-0">
              <GridPerPageSelect
                id="inc-page-size"
                pageSize={pageSize}
                onPageSizeChange={(n) => setPageSize(n as never)}
                options={PAGE_SIZE_OPTIONS}
              />
            </div>

            <div className="flex items-center justify-end gap-2 flex-wrap">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as IncrementSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated_desc">Newest first</SelectItem>
                    <SelectItem value="updated_asc">Oldest first</SelectItem>
                    <SelectItem value="effective_desc">Effective: Newest first</SelectItem>
                    <SelectItem value="effective_asc">Effective: Oldest first</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id ? "bg-primary/10 text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          ) : null}

          {!loading && displayItems.length === 0 ? (
            <div className="text-sm text-muted-foreground">No increments found for this employee / filters.</div>
          ) : null}

          {!loading && displayItems.length > 0 && viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {paginatedItems.map((item, i) => {
                const isBatch = item.kind === "batch";
                const rows = isBatch ? item.rows : [item.row];
                const employeeId = rows[0]?.employeeId ?? "";
                const name = employeeLabel(employees, employeeId);
                const photo = employeePhoto(employees, employeeId);
                const effective = rows[0]?.effectiveFrom ?? "—";
                const amountValue = rows.reduce((sum, r) => sum + (parseAmountNumber(r.newSalary) ?? 0), 0);
                const statusLabel = isBatch
                  ? `${rows.length} change${rows.length === 1 ? "" : "s"}`
                  : incrementAppliesToLabel(item.row);
                const primaryValue = isBatch
                  ? `${effective} · ${batchGroupTitle(rows)}`
                  : `${effective} · ${item.row.incrementType} · ${incLabel(item.row)}`;
                const secondaryValue = isBatch
                  ? `${rows.length} line${rows.length === 1 ? "" : "s"}`
                  : [
                      formatCurrency(parseAmountNumber(item.row.oldSalary)),
                      "→",
                      formatCurrency(parseAmountNumber(item.row.newSalary)),
                    ].join(" ");
                const reason = (rows[0]?.reason || "").trim();

                return (
                  <motion.div
                    key={isBatch ? item.batchId : item.row.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.02 }}
                  >
                    <CatalogEntityCard
                      className="h-full flex flex-col"
                      wrapText
                      title={name}
                      avatarUrl={photo}
                      titleIcon={User}
                      statusBadge={
                        isBatch
                          ? { label: statusLabel, className: "bg-amber-500/20 text-amber-700 dark:text-amber-400" }
                          : { label: statusLabel, className: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400" }
                      }
                      primaryMeta={{
                        label: "Effective",
                        value: primaryValue,
                        icon: CalendarIcon,
                        iconClassName: "text-emerald-600",
                      }}
                      secondaryMeta={{
                        label: isBatch ? "Scope" : "Change",
                        value: secondaryValue,
                        icon: isBatch ? Coins : Banknote,
                        iconClassName: isBatch ? "text-amber-600" : "text-sky-600",
                      }}
                      metaColumns={1}
                      footer={reason || undefined}
                      footerRight={formatCurrency(amountValue)}
                      footerRightClassName="text-sm font-bold tabular-nums text-emerald-700 dark:text-emerald-400"
                      onView={() => openDetails(rows)}
                      viewIconOnly
                      onEdit={() => (isBatch ? openEditBatch(rows) : openEdit(item.row))}
                      onDelete={() => (isBatch ? setDeleteBatchRows(rows) : setDeleteTarget(item.row))}
                    />
                  </motion.div>
                );
              })}
            </div>
          ) : null}

          {!loading && displayItems.length > 0 && viewMode === "list" ? (
            <div className="space-y-2">
              {paginatedItems.map((item, i) => {
                const isBatch = item.kind === "batch";
                const employeeId = isBatch ? item.rows[0]?.employeeId ?? "" : item.row.employeeId;
                const name = employeeLabel(employees, employeeId);
                const photo = employeePhoto(employees, employeeId);
                const amountValue = isBatch
                  ? item.rows.reduce((sum, r) => sum + (parseAmountNumber(r.newSalary) ?? 0), 0)
                  : parseAmountNumber(item.row.newSalary);
                const metaLine = isBatch
                  ? [
                      `Effective ${item.rows[0]?.effectiveFrom || "—"}`,
                      `${item.rows.length} change${item.rows.length === 1 ? "" : "s"}`,
                      batchGroupTitle(item.rows),
                      item.rows[0]?.reason || null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : [
                      incrementAppliesToLabel(item.row),
                      `${item.row.incrementType} · ${incLabel(item.row)}`,
                      salaryDeltaLabel(item.row),
                      item.row.effectiveFrom ? `Effective ${item.row.effectiveFrom}` : null,
                      item.row.reason || null,
                    ]
                      .filter(Boolean)
                      .join(" · ");
                const actionBtnClass =
                  "h-8 w-8 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors";
                return (
                  <motion.div
                    key={isBatch ? item.batchId : item.row.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.015 }}
                    className="glass-card px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2.5">
                      <EmployeeAvatar name={name} photoUrl={photo} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center justify-between gap-3 min-w-0 flex-1">
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-semibold text-foreground truncate min-w-0">
                                {name}
                              </h3>
                              <p className="mt-0.5 truncate text-xs text-muted-foreground" title={metaLine}>
                                {metaLine}
                              </p>
                            </div>
                            <span className="text-sm font-bold tabular-nums text-emerald-700 dark:text-emerald-400 shrink-0 text-right">
                              {formatCurrency(amountValue)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className={actionBtnClass}
                                  onClick={() => openDetails(isBatch ? item.rows : [item.row])}
                                  aria-label="View increment details"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">View details</TooltipContent>
                            </Tooltip>
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className={actionBtnClass}
                                  onClick={() => (isBatch ? openEditBatch(item.rows) : openEdit(item.row))}
                                  aria-label="Edit increment"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">Edit</TooltipContent>
                            </Tooltip>
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 w-8 p-0 border-destructive/40 text-destructive hover:border-destructive/60 hover:bg-destructive/10 hover:text-destructive transition-colors"
                                  onClick={() => (isBatch ? setDeleteBatchRows(item.rows) : setDeleteTarget(item.row))}
                                  aria-label="Delete increment"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">Delete</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : null}

          {!loading && displayItems.length > 0 ? (
            <GridPaginationFooter
              totalCount={totalCount}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          ) : null}
        </div>

        <ConfirmAlertDialog
          open={!!deleteTarget || !!deleteBatchRows?.length}
          onOpenChange={(o) => {
            if (!o) {
              setDeleteTarget(null);
              setDeleteBatchRows(null);
            }
          }}
          title={deleteBatchRows?.length ? "Delete this increment run?" : "Delete this increment?"}
          description={
            deleteBatchRows?.length ? (
              <>
                This will remove <strong className="text-foreground font-semibold">{deleteBatchRows.length}</strong> linked{" "}
                {deleteBatchRows.length === 1 ? "record" : "records"} (effective{" "}
                <strong className="text-foreground font-semibold">{deleteBatchRows[0]?.effectiveFrom}</strong>). Continue?
              </>
            ) : deleteTarget ? (
              <>
                Delete <strong className="text-foreground font-semibold">{incLabel(deleteTarget)}</strong> effective{" "}
                <strong className="text-foreground font-semibold">{deleteTarget.effectiveFrom}</strong>?
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />

        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
            <DialogHeader className="pb-3 border-b border-border/50">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-600">
                  <Coins className="w-5 h-5" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold text-foreground">
                    {detailsRows.length > 0
                      ? employeeLabel(employees, detailsRows[0]!.employeeId)
                      : "Increment details"}
                  </DialogTitle>
                  <p className="text-xs text-muted-foreground">
                    {detailsRows.length > 0
                      ? [
                          `Effective ${detailsRows[0]!.effectiveFrom}`,
                          `${detailsRows.length} change${detailsRows.length === 1 ? "" : "s"}`,
                          detailsRows[0]!.reason?.trim() || null,
                        ]
                          .filter(Boolean)
                          .join(" · ")
                      : "Salary increment details"}
                  </p>
                </div>
              </div>
            </DialogHeader>

            <div className="py-1">
              {detailsRows.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground">No details.</div>
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-foreground">Salary changes</h3>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {detailsRows.length} line{detailsRows.length === 1 ? "" : "s"}
                        {detailsRows[0]!.reason?.trim()
                          ? ` · ${detailsRows[0]!.reason!.trim()}`
                          : ""}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">
                      Effective {detailsRows[0]!.effectiveFrom}
                    </span>
                  </div>

                  <div className="overflow-hidden rounded-xl border border-border/60 shadow-sm">
                    <table className="w-full min-w-[40rem] text-sm">
                      <thead>
                        <tr className="border-b border-border/50 bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                          <th className="text-left font-semibold py-2.5 px-3">Applies to</th>
                          <th className="text-right font-semibold py-2.5 px-3">Current</th>
                          <th className="text-right font-semibold py-2.5 px-3">Increment</th>
                          <th className="text-right font-semibold py-2.5 px-3">New</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailsRows.map((r) => (
                          <tr key={r.id} className="border-b border-border/40 last:border-0">
                            <td className="py-2.5 px-3 font-medium text-foreground whitespace-nowrap">
                              {incrementAppliesToLabel(r)}
                            </td>
                            <td className="py-2.5 px-3 text-right tabular-nums text-foreground whitespace-nowrap">
                              {formatCurrency(parseAmountNumber(r.oldSalary))}
                            </td>
                            <td className="py-2.5 px-3 text-right tabular-nums text-muted-foreground whitespace-nowrap">
                              {r.incrementType === "percentage"
                                ? `${r.incrementValue}%`
                                : formatCurrency(parseAmountNumber(r.incrementValue))}
                            </td>
                            <td className="py-2.5 px-3 text-right font-semibold tabular-nums text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
                              {formatCurrency(parseAmountNumber(r.newSalary))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-muted/30 font-semibold">
                          <td className="py-2.5 px-3" colSpan={3}>
                            Total
                          </td>
                          <td className="py-2.5 px-3 text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                            {formatCurrency(
                              detailsRows.reduce((sum, r) => sum + (parseAmountNumber(r.newSalary) ?? 0), 0),
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="pt-4 border-t border-border/50 gap-2">
              <Button variant="outline" onClick={() => setDetailsOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

